import React, { useState, useEffect, useRef } from 'react';

// ── Google Places Autocomplete ───────────────────────────────────
const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';

let googleMapsLoaded = false;
let googleMapsLoadPromise: Promise<void> | null = null;

function loadGoogleMapsScript(): Promise<void> {
  if (googleMapsLoaded && (window as any).google?.maps?.places) return Promise.resolve();
  if (googleMapsLoadPromise) return googleMapsLoadPromise;
  googleMapsLoadPromise = new Promise((resolve, reject) => {
    if ((window as any).google?.maps?.places) { googleMapsLoaded = true; resolve(); return; }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => { googleMapsLoaded = true; resolve(); };
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
  return googleMapsLoadPromise;
}

export interface PlaceResult {
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  latitude: number;
  longitude: number;
}

export interface AddressSearchProps {
  value: string;
  onSelect: (place: PlaceResult) => void;
  placeholder?: string;
  inputClass: string;
  inputStyle: React.CSSProperties;
}

const AddressSearch: React.FC<AddressSearchProps> = ({ value, onSelect, placeholder, inputClass, inputStyle }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [query, setQuery] = useState(value);
  const [ready, setReady] = useState(false);

  useEffect(() => { setQuery(value); }, [value]);

  useEffect(() => {
    if (!GOOGLE_PLACES_API_KEY) return;
    loadGoogleMapsScript().then(() => {
      if (!inputRef.current || autocompleteRef.current) return;
      const google = (window as any).google;
      const ac = new google.maps.places.Autocomplete(inputRef.current, {
        types: ['establishment', 'geocode'],
        fields: ['name', 'formatted_address', 'address_components', 'geometry'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place?.geometry) return;
        const components = place.address_components || [];
        const get = (type: string) => components.find((c: any) => c.types.includes(type))?.long_name || '';
        const getShort = (type: string) => components.find((c: any) => c.types.includes(type))?.short_name || '';
        const city = get('locality') || get('sublocality') || get('administrative_area_level_2');
        const state = getShort('administrative_area_level_1');
        const result: PlaceResult = {
          name: place.name || '',
          address: place.formatted_address || '',
          city,
          state,
          country: get('country') || 'United States',
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
        };
        setQuery(place.name || place.formatted_address || '');
        onSelect(result);
      });
      autocompleteRef.current = ac;
      setReady(true);
    }).catch(() => setReady(false));
    return () => {
      if (autocompleteRef.current) {
        const google = (window as any).google;
        if (google?.maps?.event) google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [onSelect]);

  if (!GOOGLE_PLACES_API_KEY) {
    return (
      <input type="text" value={query} onChange={e => setQuery(e.target.value)}
        placeholder={placeholder || 'Address search (API key not configured)'}
        className={inputClass} style={inputStyle} disabled />
    );
  }

  return (
    <div className="relative">
      <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
        placeholder={placeholder || 'Search for an address or place...'}
        className={inputClass} style={inputStyle} />
      {!ready && <div className="text-xs mt-1" style={{ color: 'var(--rp-text-tertiary)' }}>Loading address search...</div>}
    </div>
  );
};

// ── Types ────────────────────────────────────────────────────────


export default AddressSearch;
